import { useEffect, useState } from 'react'
import { Database, Globe, Trash2 } from 'lucide-react'
import { useApp } from '@/store/AppContext'
import {
  listSharedAutomationProfiles,
  removeSharedAutomationProfile,
  syncAutomationProfilesFromBackend,
} from '@/services/automationProfileService'

export function DataControlContent() {
  const { settings, updateSettings } = useApp()
  const [profiles, setProfiles] = useState(() => listSharedAutomationProfiles(settings))

  useEffect(() => {
    setProfiles(listSharedAutomationProfiles(settings))
    let cancelled = false

    const syncProfiles = async () => {
      try {
        const synced = await syncAutomationProfilesFromBackend(null, settings)
        if (cancelled) return
        setProfiles(synced.sharedProfiles)
        const currentShared = JSON.stringify(settings.sharedAutomationProfiles ?? [])
        const nextShared = JSON.stringify(synced.sharedProfiles)
        if (currentShared !== nextShared) {
          updateSettings({ sharedAutomationProfiles: synced.sharedProfiles })
        }
      } catch {
        // keep showing cached profiles
      }
    }

    void syncProfiles()
    return () => {
      cancelled = true
    }
  }, [settings, updateSettings])

  const handleRemove = (id: string) => {
    const nextProfiles = removeSharedAutomationProfile(settings, id)
    setProfiles(nextProfiles)
    updateSettings({ sharedAutomationProfiles: nextProfiles })
  }

  return (
    <div>
      <h2 className="settings-title">Datakontroll</h2>

      <div style={{
        padding: 16,
        borderRadius: 12,
        background: '#222222',
        border: '1px solid #2A2A2A',
        marginBottom: 20,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
          <Database size={16} style={{ color: '#9A9A9A' }} />
          <span style={{ fontSize: 14, fontWeight: 600, color: '#E5E5E5' }}>Delte login- og systemprofiler</span>
        </div>
        <p style={{ fontSize: 13, lineHeight: 1.6, color: '#9A9A9A', margin: 0 }}>
          Her vises systemer du har valgt å gjøre tilgjengelige på tvers av chatter. Studio og agenten kan bruke disse
          når domenet matcher, slik at de slipper å kartlegge samme system på nytt.
        </p>
      </div>

      {profiles.length === 0 ? (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 10,
          padding: '48px 0',
          color: '#6B6B6B',
          textAlign: 'center',
        }}>
          <Globe size={28} />
          <p style={{ margin: 0, fontSize: 14, color: '#B0B0B0' }}>Ingen delte profiler ennå</p>
          <p style={{ margin: 0, fontSize: 13, maxWidth: 420, lineHeight: 1.6 }}>
            Når du lagrer en kartlagt automasjon som delt, dukker den opp her og kan gjenbrukes i andre Studio-chatter.
          </p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {profiles.map(profile => (
            <div
              key={profile.id}
              style={{
                padding: 14,
                borderRadius: 12,
                background: '#222222',
                border: '1px solid #2A2A2A',
                display: 'flex',
                justifyContent: 'space-between',
                gap: 12,
              }}
            >
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: '#E5E5E5', marginBottom: 4 }}>
                  {profile.systemName}
                </div>
                <div style={{ fontSize: 12, color: '#7A7A7A', marginBottom: 6 }}>{profile.domain}</div>
                <div style={{ fontSize: 12, color: '#B8B8B8', lineHeight: 1.6 }}>{profile.summary}</div>
                <div style={{ fontSize: 12, color: '#8F8F94', marginTop: 8, lineHeight: 1.6 }}>
                  {profile.preferredExecution}
                  {profile.status ? ` · ${profile.status}` : ''}
                  {profile.entities?.length ? ` · ${profile.entities.length} enheter` : ''}
                </div>
              </div>
              <button
                onClick={() => handleRemove(profile.id)}
                style={{
                  alignSelf: 'flex-start',
                  width: 32,
                  height: 32,
                  borderRadius: 8,
                  border: '1px solid #352020',
                  background: 'transparent',
                  color: '#E46D6D',
                  cursor: 'pointer',
                }}
                title="Fjern delt profil"
              >
                <Trash2 size={14} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
